import React from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { PHONE_COUNTRIES, formatNational, splitE164 } from '@/lib/phone'

const byIso = (iso?: string | null) =>
  PHONE_COUNTRIES.find((country) => country.iso === String(iso || '').toUpperCase()) || null

/** What the field hands back: what to store, and what to show next time. */
export interface PhoneValue {
  /** E.164, or empty when the field is empty. */
  phone: string
  /** ISO 3166-1 alpha-2 of the chosen country. */
  phoneCountry: string
}

interface PhoneInputProps {
  /** The stored number, in E.164. */
  value?: string | null
  /** The country stored with it, which wins over guessing from the prefix. */
  country?: string | null
  onChange: (value: PhoneValue) => void
  /** Used when there is no number yet: the community's country. */
  defaultCountry?: string | null
  disabled?: boolean
  required?: boolean
  id?: string
  className?: string
}

/**
 * A country and a number, which together are one value.
 *
 * Two controls rather than one free-text box, because a phone number is two
 * decisions: which country, and the number inside it. A single box makes
 * everyone type `+55` — and the ones who forget are stored as a number that
 * cannot be dialled from anywhere else.
 *
 * The country arrives chosen: from what is stored, or from the community's own
 * locale. Prefilled and editable is the whole ask — nobody in Curitiba should
 * have to find Brazil in a list of thirty-six.
 */
export const PhoneInput = ({
  value,
  country,
  onChange,
  defaultCountry,
  disabled,
  required,
  id,
  className
}: PhoneInputProps) => {
  const { t } = useTranslation('common')

  const initial = React.useMemo(() => splitE164(value, country), [value, country])
  const [iso, setIso] = React.useState(initial.country || defaultCountry || 'BR')
  const [national, setNational] = React.useState(() => formatNational(initial.country, initial.national))
  /* What this field last handed to the parent. The parent echoes it back as
     `value`; re-seeding from that echo would reformat under the caret. */
  const lastEmitted = React.useRef<string | null>(null)

  /* Re-seeded when the stored value arrives late — the dialogs load the member
     after they open — and shown with the country's mask, not as bare digits. */
  React.useEffect(() => {
    if (lastEmitted.current !== null && (value || '') === lastEmitted.current) return
    const next = splitE164(value, country)
    setNational(formatNational(next.country || defaultCountry, next.national))
    if (next.country) setIso(next.country)
    else if (defaultCountry) setIso(defaultCountry)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, country])

  const emit = (nextIso: string, nextNational: string) => {
    const digits = nextNational.replace(/\D/g, '')
    const dial = byIso(nextIso)?.dial
    const phone = digits && dial ? `+${dial}${digits}` : ''
    lastEmitted.current = phone
    onChange({
      phone,
      phoneCountry: nextIso
    })
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <select
        aria-label={t('phone.country')}
        value={iso}
        disabled={disabled}
        onChange={(event) => {
          setIso(event.target.value)
          setNational(formatNational(event.target.value, national))
          emit(event.target.value, national)
        }}
        className="h-9 w-[7.5rem] shrink-0 rounded-md border border-input bg-white px-2 text-sm outline-none focus:border-primary disabled:opacity-50"
      >
        {PHONE_COUNTRIES.map((option) => (
          <option key={option.iso} value={option.iso}>
            {option.flag} +{option.dial}
          </option>
        ))}
      </select>

      <Input
        id={id}
        type="tel"
        inputMode="tel"
        value={national}
        required={required}
        disabled={disabled}
        placeholder={t('phone.placeholder')}
        onChange={(event) => {
          /* Only digits and the separators people type. Letters in a phone
             field are a paste gone wrong, not an intention. */
          const cleaned = event.target.value.replace(/[^\d\s()-]/g, '')
          setNational(cleaned)
          emit(iso, cleaned)
        }}
        /* Masked when the person leaves the box, not while typing: a mask
           that rewrites under the caret makes deleting a separator impossible. */
        onBlur={() => setNational((current) => formatNational(iso, current))}
      />
    </div>
  )
}
